import { Button, Input, Link, Tooltip, cn } from "@heroui/react";
import { Icon } from "@iconify/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import React from "react";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useAuthAction } from "../../data";
import { type SignInSchema, signInSchema } from "../../data/schema";
import { OtpInput } from "../shared/otp-input";

export default function AuthForm({
  className,
  redirectTo,
}: {
  className?: string;
  redirectTo?: string;
  startOnSignUp?: boolean;
}) {
  const [sp] = useSearchParams();
  const { signIn } = useAuthAction();
  const [email, setEmail] = useState(sp.get("email") ?? "");
  const [[page, direction], setPage] = useState([0, 0]);
  const [isEmailValid, setIsEmailValid] = useState(true);

  const token = React.useMemo(() => sp.get("token") ?? undefined, [sp]);

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 20 : -20,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 20 : -20,
      opacity: 0,
    }),
  };

  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
  };

  const handleEmailSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validated = signInSchema.safeParse({
      intent: "sign_in:email",
      email: email,
      token: token,
    } satisfies SignInSchema);

    if (validated.error) {
      setIsEmailValid(false);
      return;
    }
    signIn.submit(validated.data);
    paginate(1);
  };

  return (
    <div
      data-testid="sign-in.card"
      className={cn(
        "flex w-full max-w-sm flex-col gap-4 rounded-large bg-content1 px-8 pb-6 pt-6 shadow-small",
        className,
      )}
    >
      <LazyMotion features={domAnimation}>
        <m.div
          layout
          className="flex min-h-[40px] justify-center gap-2 pb-2"
        >
          {page === 1 && (
            <m.div className="absolute top-2 left-2">
              <Tooltip
                content={t`Go back`}
                delay={3000}
              >
                <Button
                  isIconOnly
                  size="sm"
                  variant="flat"
                  onPress={() => paginate(-1)}
                >
                  <Icon
                    className="text-default-500"
                    icon="solar:alt-arrow-left-linear"
                    width={16}
                  />
                </Button>
              </Tooltip>
            </m.div>
          )}
          <div className="flex flex-col items-center space-y-2 flex-1">
            <m.h1 className="font-bold">
              <Trans>Welcome back!</Trans>
            </m.h1>
            <m.p className="text-sm font-normal text-default-500">
              <Trans>Please sign in to continue</Trans>
            </m.p>
            {token && (
              <m.p className="text-sm font-normal text-default-500">
                <Trans>You have been invited to join this workspace.</Trans>
              </m.p>
            )}
          </div>
        </m.div>
        <AnimatePresence
          custom={direction}
          initial={false}
          mode="wait"
        >
          <m.form
            key={page}
            animate="center"
            className="flex flex-col gap-3"
            custom={direction}
            exit="exit"
            initial="enter"
            transition={{
              duration: 0.25,
            }}
            variants={variants}
            onSubmit={handleEmailSubmit}
          >
            {page === 0 ? (
              <>
                <Input
                  data-testid="sign-in.email"
                  autoFocus
                  errorMessage={!isEmailValid ? t`Enter a valid email` : undefined}
                  isInvalid={!isEmailValid}
                  label={t`Email Address`}
                  name="email"
                  placeholder={t`Enter your email`}
                  type="email"
                  value={email}
                  variant="bordered"
                  onValueChange={(value) => {
                    setIsEmailValid(true);
                    setEmail(value);
                  }}
                />
                <Button
                  data-testid="sign-in.submit"
                  fullWidth
                  color="primary"
                  type="submit"
                >
                  {page === 0 ? t`Continue` : t`Sign In`}
                </Button>
              </>
            ) : signIn.data?.error === "api_only_account" ? (
              <p className="text-danger text-center text-sm mt-2">
                <Trans>
                  This account is not authorized to sign in to this application. Please contact
                  support.
                </Trans>
              </p>
            ) : (
              <div>
                <OtpInput
                  email={email}
                  redirectTo={redirectTo}
                  data-testid="sign-in.otp"
                />
              </div>
            )}
            <p className="text-small text-gray-500 text-center">
              <Trans>
                By signing in, you agree to our{" "}
                <Link
                  href="https://www.eversa.app/terms-of-use"
                  className="text-small"
                  target="_blank"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  href="https://www.eversa.app/eversa-privacy-policy"
                  className="text-small"
                  target="_blank"
                >
                  Privacy Policy
                </Link>
              </Trans>
            </p>
          </m.form>
        </AnimatePresence>
      </LazyMotion>
    </div>
  );
}
